// built-from: /finding-synthesis v1 impl:finding-synthesis role:implementation
import { useCallback, useEffect, useRef, useState } from 'react';
import { useEngagement } from './useEngagement';

export type FindingConfidence = 'low' | 'medium' | 'high';
export type FindingClassification = 'problem' | 'cause' | 'risk' | 'opportunity';

export interface FindingEvidenceRef {
  activityId: string;
  outputId: string;
  version: number;
}

export interface FindingUncertainty {
  id: string;
  statement: string;
  blocking: boolean;
}

export interface FindingDraft {
  findingId: string;
  title: string;
  classification: FindingClassification;
  statement: string;
  whyItMatters: string;
  confidence: FindingConfidence;
  confidenceRationale: string;
  evidence: { supports: FindingEvidenceRef[]; challenges: FindingEvidenceRef[] };
  uncertainties: FindingUncertainty[];
}

export interface FindingProposal extends Omit<FindingDraft, 'findingId'> {}

export interface FindingRecord {
  id: string;
  createId: string;
  status: 'draft' | 'confirmed';
  draft: FindingDraft;
  versions: Array<{ version: number; confirmedAt: string; confirmedBy: { id: string; name: string }; content: FindingDraft }>;
}

export interface FindingsDocument { version: number; findings: FindingRecord[] }

type FindingCommand = 'load' | 'propose' | 'create' | 'update' | 'confirm' | 'discovery';
export interface FindingCommandError { command: FindingCommand; message: string; conflict?: boolean }

export interface UseFindingSynthesisReturn {
  findings: FindingsDocument | null;
  revision: string | null;
  proposal: FindingProposal | null;
  loading: boolean;
  pending: FindingCommand | null;
  error: FindingCommandError | null;
  discoveryProposals: ReadonlySet<string>;
  loadLatestFindings: () => Promise<void>;
  clearProposal: () => void;
  proposeFinding: (evidence: FindingEvidenceRef[]) => Promise<FindingProposal | null>;
  createDraft: (createId: string, createdAt: string, createdBy: { id: string; name: string }, draft: FindingDraft) => Promise<string | null>;
  updateDraft: (findingId: string, commandId: string, patch: Partial<FindingDraft>) => Promise<boolean>;
  confirmFinding: (findingId: string, commandId: string, confirmedAt: string, confirmedBy: { id: string; name: string }) => Promise<boolean>;
  proposeDiscovery: (findingId: string, uncertaintyId: string, proposalId: string, expectedDiscoveryRevision: string | null, definitionId: string, proposedAt: string) => Promise<boolean>;
}

type FindingsResponse = { findings: FindingsDocument; revision: string };

const url = (engagementId: string): string => `/api/engagements/${encodeURIComponent(engagementId)}/findings`;
const NO_DISCOVERY_PROPOSALS: ReadonlySet<string> = new Set();

export function useFindingSynthesis(
  standaloneFindings: FindingsDocument,
  standaloneProposal: FindingProposal,
): UseFindingSynthesisReturn {
  const { currentEngagementId, loading: engagementLoading } = useEngagement();
  const fixtureFindings = useRef(standaloneFindings);
  const fixtureProposal = useRef(standaloneProposal);
  const [findings, setFindings] = useState<FindingsDocument | null>(standaloneFindings);
  const [revision, setRevision] = useState<string | null>(null);
  const [proposal, setProposal] = useState<FindingProposal | null>(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<FindingCommand | null>(null);
  const [error, setError] = useState<FindingCommandError | null>(null);

  const loadLatestFindings = useCallback(async (): Promise<void> => {
    setError(null);
    if (!currentEngagementId) {
      setFindings(fixtureFindings.current);
      setRevision(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(url(currentEngagementId));
      if (!response.ok) throw new Error('load failed');
      const body = await response.json() as FindingsResponse;
      setFindings(body.findings);
      setRevision(body.revision);
    } catch {
      setFindings(null);
      setRevision(null);
      setError({ command: 'load', message: 'Findings could not be loaded.' });
    } finally {
      setLoading(false);
    }
  }, [currentEngagementId]);

  useEffect(() => {
    if (engagementLoading) { setLoading(true); return; }
    void loadLatestFindings();
  }, [engagementLoading, loadLatestFindings]);

  const findingsCommand = useCallback(async (
    command: Exclude<FindingCommand, 'load' | 'propose' | 'discovery'>,
    path: string,
    body: Record<string, unknown>,
  ): Promise<FindingsResponse | null> => {
    setError(null);
    setPending(command);
    try {
      const response = await fetch(`${url(currentEngagementId as string)}/${path}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (response.status === 409) {
        setError({ command, message: 'This finding has changed since you opened it.', conflict: true });
        return null;
      }
      if (!response.ok) throw new Error('command failed');
      const result = await response.json() as FindingsResponse;
      setFindings(result.findings);
      setRevision(result.revision);
      return result;
    } catch {
      setError({ command, message: 'Your finding could not be saved. Your edits are still here.' });
      return null;
    } finally {
      setPending(null);
    }
  }, [currentEngagementId]);

  const proposeFinding = useCallback(async (evidence: FindingEvidenceRef[]): Promise<FindingProposal | null> => {
    setError(null); setPending('propose');
    if (!currentEngagementId) {
      setProposal(fixtureProposal.current); setPending(null); return fixtureProposal.current;
    }
    try {
      const response = await fetch(`${url(currentEngagementId)}/proposals`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ evidence }),
      });
      if (!response.ok) throw new Error('proposal failed');
      const result = await response.json() as FindingProposal;
      setProposal(result);
      return result;
    } catch {
      setError({ command: 'propose', message: 'ArcPilot could not propose an interpretation.' });
      return null;
    } finally { setPending(null); }
  }, [currentEngagementId]);

  const createDraft = useCallback(async (createId: string, createdAt: string, createdBy: { id: string; name: string }, draft: FindingDraft): Promise<string | null> => {
    if (!currentEngagementId) {
      setFindings((current) => ({ version: current?.version ?? 1, findings: [...(current?.findings ?? []), { id: draft.findingId, createId, status: 'draft', draft, versions: [] }] }));
      setProposal(null); return draft.findingId;
    }
    const result = await findingsCommand('create', 'drafts', { createId, expectedRevision: revision, createdAt, createdBy, draft });
    if (!result) return null;
    setProposal(null);
    return result.findings.findings.find((item) => item.createId === createId)?.id ?? draft.findingId;
  }, [currentEngagementId, findingsCommand, revision]);

  const updateDraft = useCallback(async (findingId: string, commandId: string, patch: Partial<FindingDraft>): Promise<boolean> => {
    if (!currentEngagementId) {
      setFindings((current) => current ? { ...current, findings: current.findings.map((item) => item.id === findingId ? { ...item, draft: { ...item.draft, ...patch } } : item) } : current);
      return true;
    }
    return Boolean(await findingsCommand('update', `${encodeURIComponent(findingId)}/draft/update`, { commandId, expectedRevision: revision, patch }));
  }, [currentEngagementId, findingsCommand, revision]);

  const confirmFinding = useCallback(async (findingId: string, commandId: string, confirmedAt: string, confirmedBy: { id: string; name: string }): Promise<boolean> => {
    if (!currentEngagementId) {
      setFindings((current) => current ? { ...current, findings: current.findings.map((item) => item.id === findingId ? { ...item, status: 'confirmed', versions: [...item.versions, { version: 1, confirmedAt, confirmedBy, content: item.draft }] } : item) } : current);
      return true;
    }
    return Boolean(await findingsCommand('confirm', `${encodeURIComponent(findingId)}/confirm`, { commandId, expectedRevision: revision, confirmedAt, confirmedBy }));
  }, [currentEngagementId, findingsCommand, revision]);

  const proposeDiscovery = useCallback(async (findingId: string, uncertaintyId: string, proposalId: string, expectedDiscoveryRevision: string | null, definitionId: string, proposedAt: string): Promise<boolean> => {
    setError(null); setPending('discovery');
    if (!currentEngagementId) { setPending(null); return true; }
    try {
      const response = await fetch(`${url(currentEngagementId)}/${encodeURIComponent(findingId)}/uncertainties/${encodeURIComponent(uncertaintyId)}/discovery-proposals`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ proposalId, expectedDiscoveryRevision, definitionId, proposedAt }),
      });
      if (response.status === 409) {
        setError({ command: 'discovery', message: 'The Discovery plan has changed. Load it and try again.', conflict: true });
        return false;
      }
      if (!response.ok) throw new Error('proposal failed');
      return true;
    } catch {
      setError({ command: 'discovery', message: 'Discovery work was not proposed. Try again.' });
      return false;
    } finally { setPending(null); }
  }, [currentEngagementId]);

  return {
    findings, revision, proposal, loading, pending, error, discoveryProposals: NO_DISCOVERY_PROPOSALS,
    loadLatestFindings, clearProposal: () => setProposal(null), proposeFinding, createDraft,
    updateDraft, confirmFinding, proposeDiscovery,
  };
}
